import dynamic from 'next/dynamic'
import { useMemo } from 'react'
import { GetStaticProps } from 'next'
// Components
import Footer from '@/components/newFooter'
import Header from '@/components/newHeader/Header'
import HeadSeo from '@/components/HeadSeo'
import Faqs from '@/components/landings/Faqs'
// Styles
import styles from './index.module.scss'
// Utils
import { getRedirectPath } from '@/components/landings/util/helper'
import { getMetaTagsData, getPageIndex } from '@/components/landings/util'
import { stateMap } from '@utils'
import { getPost } from '@/components/blog/lib/cosmic'
import { DogsLandingsContext } from '@context/dogsLandingsContext'
// GQL Queries
import {
  STATE_LIST_BY_BREED,
  BREED_LIST_BY_STATE,
  GET_LANDINGS_BREED_DETAILS,
  BREED_LIST_EXPLORER,
  GET_NEARBY_DOGS,
  GET_STATE_FACTS_STATE_LANDING,
  PUPPY_SEARCH,
  STATE_LANDING,
  GET_RELATED_BREEDS,
  GET_SEO_INDEX_RECORDS,
} from '@graphql'
import {
  BreedDetails,
  BreedListByState,
  BreedsExplorer,
  Dog,
  FaqData,
  GetSeoIndexRecordsResponse,
  LandingProps,
  LandingType,
  RelatedBreeds,
  StateFacts,
  StateLandingType,
  StateListByBreed,
} from '@/types'

const BreedLanding = dynamic(() => import('@/components/landings/BreedLanding'), { ssr: true })
const CategoryLanding = dynamic(() => import('@/components/landings/CategoryLanding'))
const StateLanding = dynamic(() => import('@/components/landings/StateLanding'))

export default function LandingPages({
  metaTags,
  // States Landing
  breedsByState,
  breedsExplorer,
  allBreedPuppies,
  categoriesData,
  nearbyDogs,
  statesData,
  // Breeds Landing
  statesByBreed,
  relatedBreeds,
  // All Landings
  breedDetails,
  breedPuppies,
  faqData,
  landingType,
  variables,
  // reservedBreed
}: LandingProps) {
  const breed = breedDetails?.breed || {}
  const memoizedNationWideBreeds = useMemo(() => allBreedPuppies?.search?.rows, [])
  const dogs = breedPuppies?.search?.rows
  const countDogs = breedPuppies?.search?.count

  return (
    <DogsLandingsContext.Provider value={{ variables, landingType }}>
      <HeadSeo metaTags={metaTags} />
      <div className={styles.main}>
        <Header />

        <BreedLanding
          dogs={dogs}
          countDogs={countDogs}
          breed={breed}
          landingType={landingType}
          statesByBreed={statesByBreed}
          relatedBreeds={relatedBreeds}
        />

        <CategoryLanding dogs={dogs} variables={variables} landingType={landingType} />

        <StateLanding
          dogs={dogs}
          categoriesData={categoriesData}
          breedsByState={breedsByState}
          breedsExplorer={breedsExplorer}
          nearbyDogs={nearbyDogs}
          statesData={statesData}
          landingType={landingType}
          variables={variables}
          breedsNationWide={memoizedNationWideBreeds}
        />

        <Faqs data={faqData} />
        <Footer />
      </div>
    </DogsLandingsContext.Provider>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<LandingProps> = async ({ params }) => {
  const apolloClient = await (await import('@/utils/apolloClient')).initializeApollo()
  const categories = [
    'kid_friendly',
    'small',
    'hypoallergenic',
    'emotional_support',
    'guard',
    'active',
    'doodle',
    'service',
    'apartment',
    'affordable',
    'therapy',
    'trained',
  ]

  // Handle breed select by id or name slug
  let variables
  let landingType: LandingType | null = null
  const slugParam = params['breed-slug']
  const slug = Array.isArray(slugParam) ? slugParam[0] : slugParam

  if (params) {
    if (categories.includes(slug)) {
      // For categories landings
      variables = { category: slug }
      landingType = 'category'
    } else if (stateMap[slug]) {
      // For states landings
      // Searches for USA states are made by the state code in state keys. The Canada state search is done by the name in stateMap keys.
      if (stateMap[slug].state.length === 2) {
        // USA
        variables = {
          state: stateMap[slug].state,
          stateName: stateMap[slug].name,
          country: 'USA',
          stateSlug: slug,
          borderingStates: stateMap[slug].borderingState,
        }
      } else {
        // Canada
        variables = {
          state: slug,
          stateName: stateMap[slug].name,
          country: 'Canada',
          stateSlug: slug,
          borderingStates: stateMap[slug].borderingState,
        }
      }
      landingType = 'state'
    } else if (!isNaN(+slug)) {
      variables = { id: +slug }
      landingType = 'breed'
    } else {
      // For breed landings
      variables = { slug }
      landingType = 'breed'
    }
  }

  // Handle redirects
  const redirectPath = await getRedirectPath(slug)

  if (redirectPath) {
    return {
      redirect: {
        destination: redirectPath,
        statusCode: 301,
      },
    }
  }

  // All landings
  let breedPuppies = {
    search: {
      rows: [],
      count: 0,
    },
  }
  let seoIndexRecords
  let allBreedPuppies = {
    search: {
      rows: [],
      count: 0,
    },
  }
  // Breed landings
  let breedDetails, statesByBreed, relatedBreeds
  // State landings
  let categoriesData, statesData, explorer, breedsByState
  const breedsData = {
    breeds: [],
  }
  let nearbyDogs = {}

  const faqData: FaqData = await getPost({
    type: 'faqs',
    slug,
    content: ['slug', 'title', 'metadata'],
  })

  try {
    // Parallelize independent queries for better performance
    const [{ data = { breed: null } }, { data: _breedPuppies }] = await Promise.all([
      apolloClient.query({
        fetchPolicy: 'no-cache',
        query: GET_LANDINGS_BREED_DETAILS,
        variables,
      }) as Promise<{ data: BreedDetails }>,
      apolloClient.query({
        fetchPolicy: 'no-cache',
        query: PUPPY_SEARCH,
        variables: {
          breedSlug: variables?.slug,
          category: variables?.category,
          state: variables?.state,
          sort: 'by-view-score',
          limit: 50,
        },
      }),
    ])

    if (variables.id && landingType === 'breed') {
      return {
        redirect: {
          destination: `/puppies-for-sale/${data.breed.slug}`,
          statusCode: 301,
        },
      }
    }

    breedDetails = data
    breedPuppies = _breedPuppies

    // eslint-disable-next-line no-console
    console.table(
      breedPuppies.search.rows.map((dog) => ({
        id: dog.id,
        name: dog.name,
        viewScore: dog.viewScore,
      }))
    )

    const { data: seoIndexRecordsData = { getSeoIndexRecords: [] } } = (await apolloClient.query({
      query: GET_SEO_INDEX_RECORDS,
    })) as { data: GetSeoIndexRecordsResponse }

    seoIndexRecords = seoIndexRecordsData?.getSeoIndexRecords || []

    // Check if the breed has dogs
    if (landingType === 'state') {
      const { data: _allBreedPuppies } = await apolloClient.query({
        fetchPolicy: 'no-cache',
        query: PUPPY_SEARCH,
        variables: {
          limit: 5,
          page: 1,
        },
      })

      allBreedPuppies = _allBreedPuppies

      const { data: _explorer } = (await apolloClient.query({
        query: BREED_LIST_EXPLORER,
        variables: { limit: 12 },
      })) as {
        data: { breedsList: BreedsExplorer }
      }

      explorer = _explorer

      const {
        data: { breeds = [], categories = {} },
      } = (await apolloClient.query({
        query: STATE_LANDING,
      })) as {
        data: StateLandingType
      }

      // Breeds
      breedsData.breeds = breeds
      // Categories
      categoriesData = categories

      // States Facts
      const { data: _stateData = {} } = (await apolloClient.query({
        query: GET_STATE_FACTS_STATE_LANDING,
        variables: { code: variables?.state },
      })) as {
        data: { state: StateFacts }
      }

      statesData = _stateData

      const { data: breedsByStateList = { breedListByState: {} } } = (await apolloClient.query({
        query: BREED_LIST_BY_STATE,
        variables: { state: variables?.state },
      })) as {
        data: { breedListByState: BreedListByState }
      }

      breedsByState = breedsByStateList

      const { data: _nearby = { nearbyStateDogs: [] } } = (await apolloClient.query({
        query: GET_NEARBY_DOGS,
        variables: { borderingStates: variables?.borderingStates },
      })) as {
        data: { nearbyStateDogs: Dog[] }
      }

      // grouped dogs by state
      const groupedDogs = {}
      _nearby?.nearbyStateDogs?.forEach((dog) => {
        if (!groupedDogs[dog.breeder.state]) {
          groupedDogs[dog.breeder.state] = []
        }
        groupedDogs[dog.breeder.state].push(dog)
      })

      nearbyDogs = groupedDogs
    }
  } catch (error) {
    console.error('ERROR LANDINGS', error)
    throw error
  }

  if (landingType === 'breed') {
    try {
      const {
        data: _statesByBreed = {
          stateListByBreed: {
            withDogs: [],
            withoutDogs: [],
          },
        },
      } = (await apolloClient.query({
        query: STATE_LIST_BY_BREED,
        variables: {
          slug: variables?.slug,
        },
      })) as {
        data: { stateListByBreed: StateListByBreed }
      }

      statesByBreed = _statesByBreed

      const { data: _relatedBreeds = { relatedBreeds: [] } } = (await apolloClient.query({
        query: GET_RELATED_BREEDS,
        variables: {
          breedId: breedDetails?.breed?.id,
          slice: 65,
          limit: 65,
        },
      })) as {
        data: { relatedBreeds: RelatedBreeds }
      }

      relatedBreeds = _relatedBreeds
    } catch (error) {
      console.error('error BREED LIST landing', error)
      throw error
    }
  }

  if (breedDetails?.breed === null) {
    return {
      notFound: true,
    }
  }

  const ROOT_URL = process?.env?.NEXT_PUBLIC_ROOT_URL

  const {
    title = '',
    description = '',
    url = '',
    image = '',
    breadCrumbSD = {},
    imageObj = '',
  } = getMetaTagsData(breedDetails?.breed || null, variables, landingType)

  const { robots = 'index, follow', canonical = '' } = getPageIndex(
    breedDetails?.breed || null,
    variables,
    landingType,
    seoIndexRecords
  )

  return {
    props: {
      metaTags: {
        title,
        description,
        url,
        image,
        breadCrumbSD,
        imageObj,
        robots: ROOT_URL === 'https://www.mawoopets.com' ? robots : 'noindex, nofollow',
        canonical,
      },
      // States Landing
      breedsByState: breedsByState?.breedListByState || {
        withDogs: [],
        withoutDogs: [],
      },
      breedsExplorer: explorer?.breedsList || {},
      categoriesData: categoriesData || {},
      nearbyDogs: nearbyDogs || {},
      allBreedPuppies: allBreedPuppies || {
        search: {
          rows: [],
          count: 0,
        },
      },
      statesData: statesData || {},
      // Breeds Landing
      statesByBreed: statesByBreed?.stateListByBreed || {},
      relatedBreeds: relatedBreeds || {},
      // All Landings
      breedDetails: breedDetails || {},
      breedPuppies: breedPuppies || {
        search: {
          rows: [],
          count: 0,
        },
      },
      faqData: faqData?.metadata?.faqs || [],
      landingType: landingType || '',
      variables: variables || {},
    },
    revalidate: 60 * 60 * 24, // Revalidate every 24 hours
  }
}
